import React from "react";
import { motion, AnimatePresence } from "framer-motion";
// --- START: เพิ่ม Imports ที่จำเป็น ---
import { format } from "date-fns";
import { th } from "date-fns/locale";
import {
  FiX,
  FiTrash2,
  FiFileText,
  FiChevronRight,
  FiArchive,
  FiClock,
  FiCalendar,
  FiLink2,
  FiDatabase,
  FiPaperclip,
} from "react-icons/fi";
// --- END: เพิ่ม Imports ---
import SafeIcon from "../common/SafeIcon"; // <-- เพิ่ม SafeIcon import

// --- START: เพิ่มฟังก์ชัน Helper ---
const formatDisplayDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  try {
    return format(date, "d MMM yy", { locale: th });
  } catch (error) {
    return dateString; // Return original if format fails
  }
};
// --- END: เพิ่มฟังก์ชัน Helper ---

const MobileHistoryModal = ({
  isOpen,
  onClose,
  meetings,
  onLoad,
  onDelete,
}) => {
  if (!isOpen) return null;

  const handleCardClick = (meetingId) => {
    onLoad(meetingId);
    // Optionally close the modal after selection
    // onClose();
  };

  const handleDeleteClick = (e, meetingId, meetingTitle) => {
    e.stopPropagation();
    onDelete(meetingId, meetingTitle);
  };

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        ></motion.div>

        <motion.div
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", stiffness: 350, damping: 35 }}
          className="relative z-10 w-full bg-gradient-to-b from-white to-slate-50 rounded-t-2xl shadow-2xl flex flex-col max-h-[85vh] sm:rounded-xl sm:max-w-lg sm:max-h-[90vh]"
        >
          <div className="flex-shrink-0 p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 id="modal-title" className="text-xl font-bold text-green-800">
              รายการที่บันทึกไว้
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-slate-500 hover:bg-green-100 hover:text-green-700 transition-all"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>

          <div className="p-3 sm:p-4 overflow-y-auto">
            {meetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <FiArchive className="w-20 h-20 text-green-200" />
                <h4 className="mt-4 text-lg font-semibold text-slate-700">
                  ไม่มีรายการที่นี่
                </h4>
                <p className="mt-1 text-sm text-slate-500">
                  ประวัติการบันทึกของคุณจะแสดงที่นี่
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {meetings.map((meeting) => {
                  // --- ดึงข้อมูลทั้งหมดเหมือนกับใน HistoryMenu ---
                  const details = meeting.meetingDetails || {};
                  const meetingDate = details.date || details.meetingDate;
                  const attachments = meeting.attachments || [];
                  return (
                    <div
                      key={meeting.id}
                      onClick={() => handleCardClick(meeting.id)}
                      className="group flex flex-col gap-2 p-3 bg-white rounded-xl shadow-sm border border-slate-200 cursor-pointer transition-all duration-300 ease-in-out hover:shadow-lg hover:border-green-400 hover:-translate-y-1"
                    >
                      {/* --- ส่วนที่ 1: Header ของ Card --- */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 bg-green-50 text-green-600 rounded-lg p-3">
                            <FiFileText className="w-5 h-5" />
                          </div>
                          <h4 className="font-semibold text-slate-800 transition-colors group-hover:text-green-600">
                            {details.meetingTitle || meeting.title}
                          </h4>
                        </div>
                        <div className="flex items-center flex-shrink-0">
                          <button
                            onClick={(e) =>
                              handleDeleteClick(e, meeting.id, meeting.title)
                            }
                            className="p-2 text-slate-400 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 hover:bg-red-100 hover:text-red-600"
                            title="ลบ"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                          <FiChevronRight className="w-6 h-6 text-slate-300 transition-all duration-300 group-hover:text-green-500 group-hover:translate-x-1" />
                        </div>
                      </div>

                      {/* --- ส่วนที่ 2: เนื้อหาที่ถอดความ --- */}
                      <p className="px-1 text-sm text-slate-600 line-clamp-3">
                        {meeting.transcript?.content || "No transcript content"}
                      </p>

                      {/* --- ส่วนที่ 3: Metadata แบบ Compact --- */}
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 pt-2 border-t border-slate-100 text-xs text-slate-500">
                        {meetingDate && (
                          <div className="flex items-center gap-1.5">
                            <FiCalendar size={12} className="text-slate-400" />
                            <span>{formatDisplayDate(meetingDate)}</span>
                          </div>
                        )}
                        {meeting.transcript?.metadata?.duration && (
                          <div className="flex items-center gap-1.5">
                            <FiClock size={12} className="text-slate-400" />
                            <span>{meeting.transcript.metadata.duration}</span>
                          </div>
                        )}
                        {meeting.transcript?.metadata?.fileSize && (
                          <div className="flex items-center gap-1.5">
                            <FiDatabase size={12} className="text-slate-400" />
                            <span>{meeting.transcript.metadata.fileSize}</span>
                          </div>
                        )}
                        {meeting.referencedMeetingIds?.length > 0 && (
                          <div className="flex items-center gap-1.5 text-green-600 font-medium">
                            <FiLink2 size={12} />
                            <span>
                              {meeting.referencedMeetingIds.length} อ้างอิง
                            </span>
                          </div>
                        )}
                        {attachments.length > 0 && (
                          <div className="flex items-center gap-1.5 text-sky-600 font-medium">
                            <FiPaperclip size={12} />
                            <span>{attachments.length} ไฟล์แนบ</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MobileHistoryModal;
